/**
 * AlternativeCitationSuggestion.jsx
 *
 * MINSKY-17131: lazy panel rendered under an above-(U) reference row (margin rail and the
 * whole-document "Find (U) Alternatives" view both use this same component) that calls
 * `findAlternativeCitation` and lets the analyst either Replace the existing citation or
 * Add the alternative alongside it.
 *
 * Purely a stateless read against the endpoint on every fetch — no Y.Map `forceRefresh` flag
 * needed, refresh just re-invokes the same call.
 */

import React, {useCallback, useEffect, useState} from 'react';
import {Alert, Box, Button, Chip, CircularProgress, IconButton, Menu, MenuItem, Stack, Tooltip, Typography} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {findAlternativeCitation} from '../../../../utils/api/aiCitationAnalysis';
import {getScoreColor, getScoreLabel} from '../../../../utils/claimAnalysisApi';
import {getClassificationByValue} from '../../helpers/classificationLevels';
import {computeLocalId} from '../../../segmentItem/utils';
import useSourcePackageBuilderStore from '../../../../stores/useSourcePackageBuilderStore';

/**
 * @param {Object}   props
 * @param {Object}   props.reference        The existing (above-U) citation being reconsidered
 * @param {string}   props.reportId
 * @param {string}   props.statementText     Statement text the citation supports
 * @param {Function} props.onReplace         (oldLocalId, suggestedSegment) => void
 * @param {Function} props.onAddAlongside     (suggestedSegment) => void
 */
export default function AlternativeCitationSuggestion({reference, reportId, statementText, onReplace, onAddAlongside, onDone}) {
    const [status, setStatus] = useState('idle'); // idle | loading | loaded | error
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [menuSegment, setMenuSegment] = useState(null);
    const [appliedLocalIds, setAppliedLocalIds] = useState(() => new Set());

    const openSourcePackageBuilder = useSourcePackageBuilderStore(s => s.open);
    const setBuilderDocument = useSourcePackageBuilderStore(s => s.setDocument);
    const isBuilderOpen = useSourcePackageBuilderStore(s => s.isOpen);

    const existingReferenceId = computeLocalId(reference) || reference?.localId || null;

    const runSearch = useCallback(() => {
        if (!reportId || !existingReferenceId) {
            setStatus('error');
            setError('Missing report or citation position — cannot search for alternatives.');
            onDone?.('error');
            return;
        }
        setStatus('loading');
        setError(null);
        const statementForSearch = statementText || reference?.content || '';
        const originalClassificationLevel = reference?.resolvedClassificationLevel ?? reference?.docClassificationLevel ?? null;
        const citationTextForRequest = reference?.content || null;
        findAlternativeCitation(reportId, statementForSearch, existingReferenceId, 'U', {
            citationText: citationTextForRequest,
            originalClassificationLevel,
        })
            .then((response) => {
                setResult(response);
                setStatus('loaded');
                onDone?.('loaded', response);
            })
            .catch((err) => {
                setError(err.message || 'Failed to search for alternative citations');
                setStatus('error');
                onDone?.('error');
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportId, existingReferenceId, statementText, reference?.content, reference?.resolvedClassificationLevel, reference?.docClassificationLevel]);

    useEffect(() => {
        runSearch();
        // Only ever auto-run once on mount (lazy fetch); refresh button re-triggers explicitly.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openMenu = (e, segment) => {
        e.stopPropagation();
        setMenuAnchor(e.currentTarget);
        setMenuSegment(segment);
    };
    const closeMenu = () => {
        setMenuAnchor(null);
        setMenuSegment(null);
    };

    // Preview a suggested source in the same document viewer used for already-added citations
    // (ReferenceListItem.handleOpenDocument) — suggestedCitations are always corpus segments, so
    // viewKind is always 'corpus' here (no MY_DATA/FILE/REPORT branching needed).
    const handlePreview = useCallback((segment) => {
        const documentId = segment?.docId ? String(segment.docId) : null;
        if (!documentId) return;
        const openArgs = {viewKind: 'corpus', documentId, segmentIndex: segment.index ?? null};
        if (isBuilderOpen) {
            setBuilderDocument(openArgs);
        } else {
            openSourcePackageBuilder(openArgs);
        }
    }, [isBuilderOpen, setBuilderDocument, openSourcePackageBuilder]);

    const handleReplace = () => {
        if (menuSegment) {
            onReplace?.(reference.localId, menuSegment);
            setAppliedLocalIds((prev) => new Set(prev).add(menuSegment.code || menuSegment.docId));
        }
        closeMenu();
    };
    const handleAddAlongside = () => {
        if (menuSegment) {
            onAddAlongside?.(menuSegment);
            setAppliedLocalIds((prev) => new Set(prev).add(menuSegment.code || menuSegment.docId));
        }
        closeMenu();
    };

    return (
        <Box sx={{px: 1.5, pb: 1.5, pt: 0.5}}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: 0.5}}>
                <Typography variant="caption" sx={{color: 'text.secondary', fontWeight: 600}}>
                    Low-side alternative sources
                </Typography>
                <Tooltip title="Search again">
                    <IconButton size="small" onClick={runSearch} disabled={status === 'loading'} sx={{width: 22, height: 22}}>
                        <RefreshIcon sx={{fontSize: 16}}/>
                    </IconButton>
                </Tooltip>
            </Stack>

            {status === 'loading' && (
                <Stack direction="row" alignItems="center" spacing={1} sx={{py: 1}}>
                    <CircularProgress size={16}/>
                    <Typography variant="caption" color="text.secondary">Searching for low-side alternatives…</Typography>
                </Stack>
            )}

            {status === 'error' && (
                <Alert severity="warning" sx={{py: 0}} onClose={undefined}>
                    <Typography variant="caption">{error}</Typography>
                </Alert>
            )}

            {status === 'loaded' && (!result?.suggestedCitations || result.suggestedCitations.length === 0) && (
                <Typography variant="caption" color="text.secondary">
                    {result?.description || 'No low-side alternatives found for this citation.'}
                </Typography>
            )}

            {status === 'loaded' && result?.suggestedCitations?.length > 0 && (
                <Stack spacing={1}>
                    {result.score != null && (
                        <Tooltip title={result.description || ''} arrow>
                            <Chip
                                label={`Best match: ${getScoreLabel(result.score)}`}
                                size="small"
                                sx={{
                                    alignSelf: 'flex-start',
                                    height: 20,
                                    fontSize: '0.7rem',
                                    bgcolor: getScoreColor(result.score),
                                    color: 'white',
                                }}
                            />
                        </Tooltip>
                    )}
                    {result.suggestedCitations.map((segment, idx) => {
                        const key = segment.code || `${segment.docId}-${idx}`;
                        const alreadyApplied = appliedLocalIds.has(segment.code || segment.docId);
                        const classLevel = getClassificationByValue(segment.docClassificationLevel || 'U');
                        return (
                            <Box
                                key={key}
                                onClick={() => handlePreview(segment)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 0.5,
                                    p: 1,
                                    borderRadius: 1,
                                    bgcolor: 'rgba(46, 125, 50, 0.04)',
                                    border: '1px solid rgba(46, 125, 50, 0.15)',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s ease',
                                    '&:hover': {bgcolor: 'rgba(46, 125, 50, 0.1)'},
                                }}
                            >
                                <Box sx={{flex: 1, minWidth: 0}}>
                                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{mb: 0.25}}>
                                        {classLevel && (
                                            <Chip
                                                label={classLevel.value}
                                                size="small"
                                                sx={{
                                                    height: 16,
                                                    fontSize: '0.6rem',
                                                    fontWeight: 700,
                                                    bgcolor: classLevel.bgColor,
                                                    color: classLevel.color,
                                                }}
                                            />
                                        )}
                                        <Typography variant="caption" sx={{fontWeight: 600, wordBreak: 'break-word'}}>
                                            {segment.docTitle || 'Suggested source'}
                                        </Typography>
                                    </Stack>
                                    {segment.content && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: 'text.secondary',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            {segment.content}
                                        </Typography>
                                    )}
                                </Box>
                                {alreadyApplied ? (
                                    <Chip label="Added" size="small" color="success" sx={{height: 20, fontSize: '0.65rem'}}/>
                                ) : (
                                    <IconButton size="small" onClick={(e) => openMenu(e, segment)} sx={{width: 24, height: 24}}>
                                        <MoreVertIcon fontSize="small"/>
                                    </IconButton>
                                )}
                            </Box>
                        );
                    })}
                </Stack>
            )}

            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
                <MenuItem onClick={handleReplace}>Replace existing citation</MenuItem>
                <MenuItem onClick={handleAddAlongside}>Add alongside existing citation</MenuItem>
            </Menu>
        </Box>
    );
}

/** Reusable "Find Low-side alternative" badge shown next to an above-(U) citation. */
export function FindAlternativeBadge({level, expanded, onClick}) {
    const classLevel = getClassificationByValue(level);
    return (
        <Button
            size="small"
            variant="outlined"
            onClick={onClick}
            sx={{
                height: 20,
                minWidth: 0,
                px: 0.75,
                fontSize: '0.65rem',
                fontWeight: 600,
                textTransform: 'none',
                lineHeight: 1,
                borderColor: classLevel?.color || 'warning.main',
                color: classLevel?.color || 'warning.main',
            }}
        >
            {level ? `${level} · ` : ''}{expanded ? 'Hide Low-side alternative' : 'Find Low-side alternative'}
        </Button>
    );
}
